import {Mental} from "../../Mental";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {MentalGameMode} from "../../server/MentalGameMode";
import {IMentalGameData, IMentalInitData} from "../../server/MentalData";
import {gsap, Linear, TimelineLite, TweenLite} from "gsap";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {MentalSoundConfig} from "../../MentalSoundConfig";
import {SoundManager} from "../../sound/SoundManager";
import {MentalReelSet} from "../../reel/MentalReelSet";
import {DeadSymbolSticker} from "../../features/DeadSymbolSticker";
import {DisplayObject} from "pixi.js";

export class MentalFsIntro extends PIXI.Container implements StageLayer {
    static FS_INTRO_TEXT: PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily: "VeteranTypewriter",
        fill: "#ffe18f",
        fontSize: "30px"
    });
    static FS_INTRO_NUM: PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily: "VeteranTypewriter",
        fill: "#ffe18f",
        fontSize: "120px"
    });
    private _button: ContinueButton;
    private _buttonCallBack: Function;
    private _introButtonContainer: PIXI.Container;

    private _normalFS: PIXI.Sprite;
    private _coolFS: PIXI.Sprite;
    private _superFS: PIXI.Sprite;
    private _fsIntroStaticImg: PIXI.Sprite;
    private _fsIntroSpine: PIXI.spine.Spine;
    private _upgradeIcon: PIXI.Sprite;
    private _textContainer: PIXI.Container;
    private _fsType: PIXI.Text;
    private _fsNum: PIXI.Text;
    private _overlay: PIXI.Graphics;
    private _gameData: IMentalInitData | IMentalGameData;

    private _isFSUpgrade: boolean;
    private readonly _autoHideTime: number = 2.5;
    private readonly _countUpTime: number = 1.5;
    private readonly _countUpDelay: number = 0.5;

    constructor() {
        super();
        this.initAnimations();
    }

    private static createOverlay(): PIXI.Graphics {
        let mask: PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0x000000, 0.75);
        mask.drawRect(-700, -700, 3600, 3600);
        mask.endFill();
        mask.alpha = 0;

        return mask;
    }

    private static createSprite(assetName: string): PIXI.Sprite {
        let introSprite: PIXI.Sprite = new PIXI.Sprite(Mental.assetLoader.getTextures(assetName)[0]);
        introSprite.alpha = 0;
        introSprite.anchor.set(0.5);
        introSprite.position.set(360);

        return introSprite;
    }

    private static createSpine(): PIXI.spine.Spine {
        const introSpine: PIXI.spine.Spine = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset("fsIntro"));
        introSpine.position.set(360, 130);
        introSpine.alpha = 0;
        introSpine.visible = false;
        return introSpine;
    }

    public onResize() {
        if (NolimitApplication.isLandscape) {
            this._introButtonContainer.position.set(360, 615);
        } else {
            const centerBottom: number = (NolimitApplication.screenBounds.bottom - 610) * 0.5 + 610;
            this._introButtonContainer.position.set(360, centerBottom);
        }
    }

    public showIntro(isFSUpgrade: boolean, callBackFn: Function): void {
        this._isFSUpgrade = isFSUpgrade;
        Mental.keypadPlugin.hide();

        if (Mental.model.data.nextMode !== MentalGameMode.STANDARD_FREESPIN) {
            Mental.deadFeatureStickyMultiplier.showSymbolStickers();
        }

        this._buttonCallBack = callBackFn;
        if (isFSUpgrade) {
            const tl: TimelineLite = new TimelineLite({
                onComplete: () => {
                    tl.pause().clear().kill();
                }
            });
            tl.to(this, {
                delay: this._autoHideTime, onComplete: () => {
                    this.onButtonClick(isFSUpgrade);
                }
            });
            this.showIntroContent();
        } else {
            this.showIntroSpine();
        }
    }

    protected initContinueButton(): PIXI.Container {
        this._introButtonContainer?.removeChildren();
        const introContainer: PIXI.Container = new PIXI.Container();

        this._button = this.createButton();
        introContainer.visible = false;
        introContainer.addChild(this._button);
        return introContainer;
    }

    private createButton(): ContinueButton {
        let continueBtn: ContinueButton = this.addChild(new ContinueButton());
        continueBtn.x -= continueBtn.width / 2;
        continueBtn.y -= continueBtn.height / 2;
        continueBtn.addClickCallback(() => this.onButtonClick(false));
        continueBtn.enable(true);

        return continueBtn;
    }

    private resetIntroAssets(dur: number): void {
        this._fsNum.text = "";
        this._fsType.text = "";
        TweenLite.to([this._overlay, this._normalFS, this._coolFS, this._superFS, this._upgradeIcon, this._textContainer], dur, {alpha: 0});
    }

    private resetIntroSpine(): void {
        this._fsIntroSpine.updateTransform();
        this._fsIntroSpine.visible = false;
        this._fsIntroSpine.alpha = 0;
        this._fsIntroSpine.skeleton.setToSetupPose();
        this._fsIntroSpine.state.setEmptyAnimation(0, 0);
        this._fsIntroSpine.state.clearTracks();
    }

    private getIntroSprite(): PIXI.Sprite {
        let introSprite: PIXI.Sprite,
            nextMode: string = this.getNextMode();

        if (nextMode === MentalGameMode.COOL_FREESPIN) {
            introSprite = this._coolFS;
        } else if (nextMode === MentalGameMode.SUPER_FREESPIN) {
            introSprite = this._superFS;
        } else {
            introSprite = this._normalFS;
        }

        return introSprite;
    }

    private getIntroAnimName(): string {
        let animName: string,
            nextMode: string = this.getNextMode();

        if (nextMode === MentalGameMode.COOL_FREESPIN) {
            animName = "lobotomy_freespin";
        } else if (nextMode === MentalGameMode.SUPER_FREESPIN) {
            animName = "Mental_freespin";
        } else {
            animName = "autopsy_freespin";
        }

        return animName;
    }

    private getIntroText(): string {
        let introText: string,
            nextMode: string = this.getNextMode();

        if (nextMode === MentalGameMode.COOL_FREESPIN) {
            introText = Mental.apiPlugin.translations.translate(Mental.skinName()==="us-nonfree"?"Lobotomy Bonus Spins":"Lobotomy Freespins");
        } else if (nextMode === MentalGameMode.SUPER_FREESPIN) {
            introText = Mental.apiPlugin.translations.translate(Mental.skinName()==="us-nonfree"?"Mental Bonus Spins":"Mental Freespins");
        } else {
            introText = Mental.apiPlugin.translations.translate(Mental.skinName()==="us-nonfree"?"Autopsy Bonus Spins":"Autopsy Freespins");
        }

        return introText;
    }

    private getIntroTextPos(): number[] {
        let textPos: number[],
            nextMode: string = this.getNextMode();

        if (nextMode === MentalGameMode.COOL_FREESPIN) {
            textPos = [0, 15];
        } else if (nextMode === MentalGameMode.SUPER_FREESPIN) {
            textPos = [-160, 75];
        } else {
            textPos = [0, 20];
        }

        return textPos;
    }

    private getNumPos(): number[] {
        let numPos: number[],
            nextMode: string = this.getNextMode();

        if (nextMode === MentalGameMode.COOL_FREESPIN) {
            numPos = [0, -50];
        } else if (nextMode === MentalGameMode.SUPER_FREESPIN) {
            numPos = [-160, 0];
        } else {
            numPos = [0, -40];
        }

        return numPos;
    }

    private getNextMode(): string {
        return this._gameData.nextMode;
    }

    private setIntroText(): void {
        this._fsType.text = this.getIntroText();

        const introTextPos: number[] = this.getIntroTextPos();

        this._fsType.anchor.set(0.5);
        this._fsType.position.set(introTextPos[0], introTextPos[1]);
    }

    private setFSNumber(): void {
        let toVal: number = this._gameData.freespinsLeft,
            initValue: number = this._isFSUpgrade ? (toVal - this._gameData.addedNumberOfFreespinsThisSpin) : toVal,
            fromVal: { currVal: number } = {currVal: initValue},
            numPos: number[] = this.getNumPos();

        this._fsNum.text = initValue + "";
        this._fsNum.anchor.set(0.5);
        this._fsNum.position.set(numPos[0], numPos[1]);

        TweenLite.to(fromVal, this._countUpTime, {
            currVal: toVal,
            delay: this._countUpDelay,
            roundProps: "currVal",
            ease: Linear.easeOut,
            onUpdate: function () {
                if (Mental.fsIntro._isFSUpgrade && (fromVal.currVal > parseInt(Mental.fsIntro._fsNum.text))) {
                    SoundManager.playSoundEffect(MentalSoundConfig.FS_UPGRADE_COUNTER);
                }
                Mental.fsIntro._fsNum.text = fromVal.currVal.toString();
            }
        });
    }

    private setUpgradeIcon(): void {
        this._upgradeIcon.scale.set(0.49);
        this._upgradeIcon.rotation = -25.44;
        this._upgradeIcon.position.set(200, 220);
        if (this._isFSUpgrade) {
            TweenLite.to(this._upgradeIcon, 0.5, {alpha: 1});
        }
    }

    private showIntroSpine(): void {
        this.resetIntroSpine();
        this._gameData = Mental.model.data;
        this._fsIntroSpine.visible = true;

        const tl: TimelineLite = new TimelineLite(),
            animName: string = this.getIntroAnimName(),
            stillName: string = animName.toLowerCase().replace("freespin", "still");

        this._fsIntroStaticImg.texture = Mental.assetLoader.getTextures(stillName)[0];

        tl.add(TweenLite.to(this._fsIntroStaticImg, {
            alpha: 1,
            duration: 0.1,
            onComplete: () => {
                this._fsIntroSpine.alpha = 1;
                this.playDoorOpenSound(animName);
                SoundManager.playFSIntroLoop(MentalSoundConfig.FS_INTRO);
                this._fsIntroSpine.state.setAnimation(0, animName, false);
                TweenLite.to(this._fsIntroStaticImg, {
                    alpha: 0,
                    duration: 0.01
                });
            }
        }));

        tl.to({}, {
            duration: 1.667, //door closing at 50th frame so 50/30 fps
            onComplete: () => {
                this.showIntroContent();
                SoundManager.playSoundEffect(MentalSoundConfig.FS_DOOR_CLOSE);
            }
        });
    }

    private playDoorOpenSound(animName: string): void {
        let fsMode: string = animName.toLowerCase().replace("_freespin", ""),
            soundToPlay: string;

        switch (fsMode) {
            case "lobotomy":
                soundToPlay = MentalSoundConfig.FS_LOBOTOMY_DOOR_OPEN;
                break;
            case "mental":
                soundToPlay = MentalSoundConfig.FS_MENTAL_DOOR_OPEN;
                break;
            default:
                soundToPlay = MentalSoundConfig.FS_AUTOPSY_DOOR_OPEN;
                break;
        }
        SoundManager.playSoundEffect(soundToPlay);
    }

    private showIntroContent(): void {
        this.resetIntroAssets(0.01);
        this._gameData = Mental.model.data;

        let introSprite: PIXI.Sprite = this.getIntroSprite();
        this.setIntroText();
        this.setFSNumber();
        this.setUpgradeIcon();

        const fadeInTime: number = this._isFSUpgrade ? 0.5 : 0.35;

        TweenLite.to([this._overlay, introSprite, this._textContainer], fadeInTime, {
            alpha: 1,
            onStart:()=>{
                if(this._isFSUpgrade){
                    this.triggerFSAmbienceSound(this._isFSUpgrade);
                }
            },
            onComplete: () => {
                if (!this._isFSUpgrade) {
                    this._introButtonContainer.visible = true;
                }
            }
        });

        //#271
        MentalReelSet.updateReelSet();

        // #262
        const deadStickers: DisplayObject[] = Mental.deadFeatureStickyMultiplier.children;
        deadStickers.forEach((sticker: PIXI.DisplayObject) => {
            (<DeadSymbolSticker>sticker).showLocks();
        });
    }

    private initAnimations() {
        this._overlay = MentalFsIntro.createOverlay();

        this._normalFS = MentalFsIntro.createSprite("normalSprite");
        this._coolFS = MentalFsIntro.createSprite("coolSprite");
        this._superFS = MentalFsIntro.createSprite("superSprite");

        this._fsIntroStaticImg = MentalFsIntro.createSprite("autopsy_still");
        this._fsIntroStaticImg.position.y = 130;

        this._fsIntroSpine = MentalFsIntro.createSpine();
        this._upgradeIcon = MentalFsIntro.createSprite("upgradeImg");

        this._textContainer = this.createTextContainer();
        this._introButtonContainer = this.initContinueButton();

        this.addChild(this._overlay, this._fsIntroSpine, this._fsIntroStaticImg, this._normalFS, this._coolFS, this._superFS, this._upgradeIcon, this._textContainer, this._introButtonContainer);
        this.onResize();
    }

    private createTextContainer(): PIXI.Container {
        let textContainer: PIXI.Container = new PIXI.Container();
        textContainer.position.set(360);
        textContainer.alpha = 0;

        this._fsType = new PIXI.Text("", MentalFsIntro.FS_INTRO_TEXT);
        this._fsNum = new PIXI.Text("", MentalFsIntro.FS_INTRO_NUM);

        textContainer.addChild(this._fsType, this._fsNum);

        return textContainer;
    }

    private onButtonClick(isFSUpgrade: boolean): void {
        Mental.keypadPlugin.show();
        this._introButtonContainer.visible = false;
        this._buttonCallBack();
        this.resetIntroSpine();
        this.resetIntroAssets(0.5);

        // #228
        if (!isFSUpgrade) {
            SoundManager.playSoundEffect(MentalSoundConfig.FS_BTN_CLICK);

            // #138
            this.triggerFSAmbienceSound(isFSUpgrade);
        }
    }

    // #138
    private triggerFSAmbienceSound(isFSUpgrade: boolean): void {
        const nextMode: string = this.getNextMode();

        let soundName: string;

        if (nextMode === MentalGameMode.COOL_FREESPIN) {
            soundName = MentalSoundConfig.COOL_FS_AMBIANCE;
        } else if (nextMode === MentalGameMode.SUPER_FREESPIN) {
            soundName = MentalSoundConfig.SUPER_FS_AMBIANCE;
        } else {
            soundName = MentalSoundConfig.STANDARD_FS_AMBIANCE;
        }

        if (isFSUpgrade) {
            SoundManager.upgradeFSMusic(soundName);
        } else {
            SoundManager.startFSMusic(soundName);
        }
    }
}